import React, {useState} from "react";
import {Calendar} from "primereact/calendar";

export type DateRangeFilterValue = {
    fromDate: Date | null;
    toDate: Date | null;
    fromTime: Date | null;
    toTime: Date | null;
};

export const getDefaultFilterStartDate = () => new Date();
export const getDefaultFilterEndDate = () => new Date();

export const getDefaultFilterStartTime = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
};

export const getDefaultFilterEndTime = () => {
    const date = new Date();
    date.setHours(23, 59, 59, 0);
    return date;
};

export const getDefaultDateRangeFilterValue = (): DateRangeFilterValue => ({
    fromDate: getDefaultFilterStartDate(),
    toDate: getDefaultFilterEndDate(),
    fromTime: getDefaultFilterStartTime(),
    toTime: getDefaultFilterEndTime(),
});

const minDate = new Date(2023, 5, 1);

const bindDateInput = (input: HTMLInputElement, setter: (value: Date | null) => void) => {
    const digits = input.value.replace(/\D/g, "").slice(0, 8);
    const dd = digits.slice(0, 2);
    const mm = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    const display = (dd || "") + (digits.length > 2 ? "/" + mm : "") + (digits.length > 4 ? "/" + yyyy : "");
    const caret = digits.length + (digits.length > 2 ? 1 : 0) + (digits.length > 4 ? 1 : 0);

    if (input.value !== display) {
        input.value = display;
        input.setSelectionRange(caret, caret);
    }

    if (digits.length === 8) {
        const day = parseInt(dd, 10);
        const month = parseInt(mm, 10) - 1;
        const year = parseInt(yyyy, 10);
        const parsed = new Date(year, month, day);
        const valid = !isNaN(parsed.getTime())
            && parsed.getDate() === day
            && parsed.getMonth() === month
            && parsed.getFullYear() === year
            && parsed >= minDate;

        setter(valid ? parsed : null);
    }
};

const bindTimeInput = (input: HTMLInputElement, currentValue: Date | null, setter: (value: Date) => void) => {
    const digits = input.value.replace(/\D/g, "").slice(0, 4);
    const HH = digits.slice(0, 2);
    const MM = digits.slice(2, 4);
    const display = HH + (digits.length > 2 ? ":" + MM : "");

    if (input.value !== display) {
        const caret = digits.length + (digits.length > 2 ? 1 : 0);
        input.value = display;
        input.setSelectionRange(caret, caret);
    }

    if (digits.length === 4) {
        const hours = parseInt(HH, 10);
        const minutes = parseInt(MM, 10);
        if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
            const base = currentValue ? new Date(currentValue) : new Date();
            base.setHours(hours, minutes, 0, 0);
            setter(base);
        }
    }
};

type DateRangeFilterTemplateProps = {
    value: DateRangeFilterValue;
    onChange: (next: DateRangeFilterValue) => void;
};

const DateRangeFilterTemplate = ({value, onChange}: DateRangeFilterTemplateProps) => {
    const [focusedField, setFocusedField] = useState<string | null>(null);
    const fromDate = value.fromDate || getDefaultFilterStartDate();
    const toDate = value.toDate || getDefaultFilterEndDate();
    const fromTime = value.fromTime || getDefaultFilterStartTime();
    const toTime = value.toTime || getDefaultFilterEndTime();

    const patch = (partial: Partial<DateRangeFilterValue>) => onChange({...value, ...partial});

    return (
        <>
            <span className="title">Виберіть дату</span>
            <div className="flex field align-items-center max-w-15rem">
                <span className="title" style={{width: '35px'}}>З*</span>
                <Calendar
                    className="mt-2"
                    value={focusedField === 'fromDate' ? null : fromDate}
                    onChange={(e: any) => patch({fromDate: e.value})}
                    onInput={(e: any) => bindDateInput(e.target, (next) => patch({fromDate: next}))}
                    onFocus={() => setFocusedField('fromDate')}
                    onBlur={(e: any) => {
                        setFocusedField(null);
                        const digits = e.target.value.replace(/\D/g, "");
                        if (digits.length !== 8) {
                            e.target.value = "";
                            patch({fromDate: getDefaultFilterStartDate()});
                        } else if (!value.fromDate) {
                            patch({fromDate: getDefaultFilterStartDate()});
                        }
                    }}
                    minDate={minDate}
                    maxDate={new Date()}
                    dateFormat="dd/mm/yy"
                    placeholder="дд/мм/рр"
                />
            </div>
            <div className="flex field align-items-center max-w-15rem">
                <span className="title" style={{width: '35px'}}>По*</span>
                <Calendar
                    className="mt-2"
                    value={focusedField === 'toDate' ? null : toDate}
                    onChange={(e: any) => patch({toDate: e.value})}
                    onInput={(e: any) => bindDateInput(e.target, (next) => patch({toDate: next}))}
                    onFocus={() => setFocusedField('toDate')}
                    onBlur={(e: any) => {
                        setFocusedField(null);
                        const digits = e.target.value.replace(/\D/g, "");
                        if (digits.length !== 8) {
                            e.target.value = "";
                            patch({toDate: getDefaultFilterEndDate()});
                        } else if (!value.toDate) {
                            patch({toDate: getDefaultFilterEndDate()});
                        }
                    }}
                    minDate={minDate}
                    maxDate={new Date()}
                    dateFormat="dd/mm/yy"
                    placeholder="дд/мм/рр"
                />
            </div>
            <div className="flex max-w-15rem">
                <div className="md:col-6">
                    <div className="time-item">
                        <span className="title">З*</span>
                        <Calendar
                            appendTo="self"
                            placeholder="00:00"
                            value={focusedField === 'fromTime' ? null : fromTime}
                            timeOnly
                            hourFormat="24"
                            onChange={(e) => patch({fromTime: e.value as Date})}
                            onFocus={() => setFocusedField('fromTime')}
                            onInput={(e: any) => bindTimeInput(e.target, value.fromTime, (next) => patch({fromTime: next}))}
                            onBlur={() => {
                                setFocusedField(null);
                                if (!value.fromTime) {
                                    patch({fromTime: getDefaultFilterStartTime()});
                                }
                            }}
                        />
                    </div>
                </div>
                <div className="md:col-6">
                    <div className="time-item">
                        <span className="title">По*</span>
                        <Calendar
                            appendTo="self"
                            placeholder="00:00"
                            value={focusedField === 'toTime' ? null : toTime}
                            timeOnly
                            hourFormat="24"
                            onChange={(e) => patch({toTime: e.value as Date})}
                            onFocus={() => setFocusedField('toTime')}
                            onInput={(e: any) => bindTimeInput(e.target, value.toTime, (next) => patch({toTime: next}))}
                            onBlur={() => {
                                setFocusedField(null);
                                if (!value.toTime) {
                                    patch({toTime: getDefaultFilterEndTime()});
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default DateRangeFilterTemplate;
